import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import '../../config/api';
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Circle, Loader2, Play, Settings2 } from 'lucide-react';
import { useBusinessDate } from '../../contexts/BusinessDateContext';
import DateField from '../shared/DateField';

/**
 * Admin › Batch Jobs › End of day
 *
 * The end-of-day run of the book: the business date it will close, the steps in order, and one
 * action. The run closes the book for its business date and advances it to the next one; every
 * step is handed that date. If nobody starts it, the schedule does at the book's end-of-day time.
 */

type StepStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'WARNING' | 'FAILED';

interface CatalogStep { order: number; code: string; name: string; blocking: boolean; }

interface RunStep {
  step_order: number; step_code: string; step_name: string; blocking: boolean; status: StepStatus;
  attempts: number; duration_ms: number | null; result: Record<string, unknown> | null; error: string | null;
}

interface Run {
  id: number; business_date: string; next_date: string; status: 'RUNNING' | 'FAILED' | 'COMPLETE';
  triggered_by: string; started_at: string; finished_at: string | null; failed_step: string | null;
  error: string | null; steps: RunStep[];
}

interface Status {
  tenantId: number;
  businessDate: string; previousBusinessDate: string; nextBusinessDate: string; tradeDate: string;
  status: 'OPEN' | 'EOD_RUNNING' | 'EOD_FAILED'; rollCalendar: string; autoRoll: boolean; eodTime: string;
  source: string; wallClockDate: string; due: boolean; inProgress: boolean; steps: CatalogStep[];
  latestRun: Run | null;
}

interface HistoryRow { id: number; business_date: string; next_date: string; status: string; triggered_by: string;
  started_at: string; finished_at: string | null; failed_step: string | null; }

const longDate = (iso?: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const clock = (time?: string) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const took = (ms: number | null) => (ms == null ? '' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);

const errorText = (e: unknown): string => {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string; message?: string } | undefined;
    return data?.error || data?.message || e.message;
  }
  return e instanceof Error ? e.message : String(e);
};

const StepIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
  if (status === 'SUCCESS') return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
  if (status === 'WARNING') return <AlertTriangle className="w-4 h-4 text-amber-300" />;
  if (status === 'FAILED') return <AlertCircle className="w-4 h-4 text-rose-400" />;
  if (status === 'RUNNING') return <Loader2 className="w-4 h-4 text-blue-300 animate-spin" />;
  return <Circle className="w-4 h-4 text-slate-600" />;
};

const STEP_WORDS: Record<StepStatus, string> = {
  PENDING: 'Not started', RUNNING: 'Running', SUCCESS: 'Done', WARNING: 'Done, with warnings', FAILED: 'Stopped here',
};

const EndOfDayPanel: React.FC = () => {
  const { refresh: refreshBusinessDate } = useBusinessDate();
  const [status, setStatus] = useState<Status | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [placeDate, setPlaceDate] = useState('');
  const [placeReason, setPlaceReason] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const lastState = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get<Status>('/api/eod/status'),
        axios.get<HistoryRow[]>('/api/eod/runs', { params: { limit: 10 } }),
      ]);
      setStatus(s.data);
      setHistory(h.data);
      // The header shows "Closing" while a run is in progress, so it follows the book's state as
      // well as its date (F-BD-008).
      const state = `${s.data.businessDate}|${s.data.status}`;
      if (lastState.current && lastState.current !== state) refreshBusinessDate();
      lastState.current = state;
    } catch (e) {
      setError(errorText(e));
    }
  }, [refreshBusinessDate]);

  useEffect(() => { load(); }, [load]);

  const busy = status?.status === 'EOD_RUNNING' || starting;
  useEffect(() => {
    if (!busy) return undefined;
    const timer = window.setInterval(load, 2000);
    return () => window.clearInterval(timer);
  }, [busy, load]);

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      await axios.post('/api/eod/run');
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  const placeBook = async () => {
    if (!status) return;
    setSaving(true);
    setError(null);
    setSettingsMessage(null);
    try {
      await axios.put(`/api/tenant-config/${status.tenantId}/business-date`, { businessDate: placeDate, reason: placeReason });
      setSettingsMessage(`The book is now on ${longDate(placeDate)}. The schedule no longer rolls it until you turn it back on below.`);
      setPlaceDate('');
      setPlaceReason('');
      await load();
      refreshBusinessDate();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const saveSchedule = async (autoRoll: boolean) => {
    if (!status) return;
    setSaving(true);
    setError(null);
    setSettingsMessage(null);
    try {
      await axios.put(`/api/tenant-config/${status.tenantId}/business-date/schedule`,
        { autoRoll, eodTime: scheduleTime || status.eodTime });
      setSettingsMessage(autoRoll
        ? `The schedule starts end of day at ${clock(scheduleTime || status.eodTime)} if nobody has.`
        : 'The schedule no longer starts end of day; an operator rolls this book.');
      setScheduleTime('');
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  if (!status) {
    return (
      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-5 text-sm text-slate-400" data-testid="eod-panel">
        {error ? <span className="text-rose-400 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</span>
          : <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the book…</span>}
      </div>
    );
  }

  const failed = status.status === 'EOD_FAILED';
  const running = status.status === 'EOD_RUNNING';
  // The latest run belongs to this business date only while the book is still on it.
  const run = status.latestRun && status.latestRun.business_date === status.businessDate ? status.latestRun : null;
  const lastClosed = status.latestRun && status.latestRun.status === 'COMPLETE' ? status.latestRun : null;
  const byCode: Record<string, RunStep> = {};
  (run?.steps || []).forEach(step => { byCode[step.step_code] = step; });

  const headline = running
    ? `Closing ${longDate(status.businessDate)}`
    : failed
      ? `End of day for ${longDate(status.businessDate)} stopped`
      : `The book is open for ${longDate(status.businessDate)}`;

  const detail = running
    ? `New tickets default to ${longDate(status.nextBusinessDate)}. The date moves when the last step finishes.`
    : failed
      ? `It stopped at “${run?.steps.find(s => s.status === 'FAILED')?.step_name || run?.failed_step || 'a step'}”. The book stays on this date until the run finishes. Finished steps are not run again.`
      : status.autoRoll
        ? `If nobody runs it, end of day starts at ${clock(status.eodTime)} and the book moves to ${longDate(status.nextBusinessDate)}.`
        : `This book is rolled by an operator; the schedule does not start end of day. Running it moves the book to ${longDate(status.nextBusinessDate)}.`;

  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 overflow-hidden" data-testid="eod-panel">
      <div className="p-5 flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">End of day</p>
          <h2 className="text-lg font-bold text-white mt-1" data-testid="eod-headline">{headline}</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">{detail}</p>
          {error && <p className="mt-3 text-sm text-rose-400 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</p>}
          {failed && run?.error && (
            <p className="mt-3 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{run.error}</p>
          )}
        </div>
        <dl className="grid grid-cols-3 gap-x-6 gap-y-1 text-sm">
          <dt className="text-xs text-slate-500">Previous</dt>
          <dt className="text-xs text-slate-500">Business date</dt>
          <dt className="text-xs text-slate-500">Next</dt>
          <dd className="text-slate-300">{longDate(status.previousBusinessDate)}</dd>
          <dd className="text-white font-semibold" data-testid="eod-business-date">{longDate(status.businessDate)}</dd>
          <dd className="text-slate-300">{longDate(status.nextBusinessDate)}</dd>
        </dl>
        <button
          onClick={start}
          disabled={busy}
          data-testid="eod-run"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400 text-white text-sm font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running…' : failed ? 'Resume end of day' : 'Run end of day'}
        </button>
      </div>

      <ol className="border-t border-slate-700/60 divide-y divide-slate-800">
        {status.steps.map(step => {
          const done = byCode[step.code];
          const state: StepStatus = done?.status || 'PENDING';
          const problems = (done?.result?.problems as string[] | undefined) || [];
          const expandable = !!done && (problems.length > 0 || !!done.error || !!done.result);
          return (
            <li key={step.code} data-testid={`eod-step-${step.code}`}>
              <button
                type="button"
                disabled={!expandable}
                onClick={() => setOpen(o => ({ ...o, [step.code]: !o[step.code] }))}
                className="w-full px-5 py-2.5 flex items-center gap-3 text-left text-sm disabled:cursor-default"
              >
                <StepIcon status={state} />
                <span className="w-5 text-slate-500 tabular-nums">{step.order}</span>
                <span className="text-slate-200 flex-1">{step.name}</span>
                <span className={state === 'FAILED' ? 'text-rose-300' : state === 'WARNING' ? 'text-amber-300' : 'text-slate-500'}>
                  {STEP_WORDS[state]}{done?.duration_ms != null && state !== 'RUNNING' ? ` · ${took(done.duration_ms)}` : ''}
                </span>
                {expandable ? (open[step.code] ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />) : <span className="w-4" />}
              </button>
              {expandable && open[step.code] && (
                <div className="px-5 pb-4 pl-16 space-y-2">
                  {done?.error && <p className="text-sm text-rose-300">{done.error}</p>}
                  {problems.map(problem => <p key={problem} className="text-sm text-amber-200">{problem}</p>)}
                  {done?.result && (
                    <pre className="text-xs text-slate-400 bg-slate-950/60 border border-slate-800 rounded-lg p-3 overflow-x-auto max-h-64">
                      {JSON.stringify(done.result, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="border-t border-slate-700/60 px-5 py-3 text-sm">
        <button type="button" onClick={() => setShowHistory(v => !v)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200" data-testid="eod-history-toggle">
          {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {lastClosed && !run
            ? `Last closed ${longDate(lastClosed.business_date)} by ${lastClosed.triggered_by === 'SCHEDULE' ? 'the schedule' : lastClosed.triggered_by}`
            : 'Earlier runs'}
        </button>
        {showHistory && (
          history.length === 0 ? <p className="mt-2 text-slate-500">No end-of-day run has been recorded for this book yet.</p> : (
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-1 pr-4">Business date</th><th className="py-1 pr-4">Rolled to</th>
                  <th className="py-1 pr-4">Result</th><th className="py-1 pr-4">Started by</th><th className="py-1">Finished</th>
                </tr>
              </thead>
              <tbody>
                {history.map(row => (
                  <tr key={row.id} className="border-t border-slate-800 text-slate-300">
                    <td className="py-1.5 pr-4">{longDate(row.business_date)}</td>
                    <td className="py-1.5 pr-4">{row.status === 'COMPLETE' ? longDate(row.next_date) : '—'}</td>
                    <td className={`py-1.5 pr-4 ${row.status === 'FAILED' ? 'text-rose-300' : row.status === 'COMPLETE' ? 'text-emerald-300' : 'text-amber-300'}`}>
                      {row.status === 'COMPLETE' ? 'Closed' : row.status === 'FAILED' ? `Stopped at ${row.failed_step || 'a step'}` : 'Running'}
                    </td>
                    <td className="py-1.5 pr-4">{row.triggered_by === 'SCHEDULE' ? 'Schedule' : row.triggered_by}</td>
                    <td className="py-1.5">{row.finished_at ? new Date(row.finished_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      <div className="border-t border-slate-700/60 px-5 py-3 text-sm">
        <button type="button" onClick={() => setShowSettings(v => !v)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200" data-testid="eod-settings-toggle">
          {showSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Settings2 className="w-4 h-4" /> Book date and schedule
        </button>
        {showSettings && (
          <div className="mt-3 grid gap-5 md:grid-cols-2">
            <div className="space-y-2" data-testid="eod-place">
              <p className="text-slate-200 font-semibold">Place the book on a date</p>
              <p className="text-xs text-slate-500">
                For demonstration and test books. The date must be a business day on {status.rollCalendar}; a book
                holding trades or positions after the date cannot be moved back over them. The schedule stops
                rolling the book until you turn it back on. Production books move only through end of day.
              </p>
              <DateField value={placeDate} onChange={setPlaceDate} calendars={[status.rollCalendar]}
                disabled={saving || running} aria-label="New business date" />
              <input
                value={placeReason}
                onChange={e => setPlaceReason(e.target.value)}
                disabled={saving || running}
                placeholder="Reason (required)"
                className="w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-slate-200 placeholder:text-slate-600"
              />
              <button
                type="button"
                onClick={placeBook}
                disabled={saving || running || !placeDate || !placeReason.trim()}
                className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white text-sm font-semibold"
                data-testid="eod-place-submit"
              >
                Place the book on {placeDate ? longDate(placeDate) : 'this date'}
              </button>
            </div>
            <div className="space-y-2" data-testid="eod-schedule">
              <p className="text-slate-200 font-semibold">Schedule</p>
              <p className="text-xs text-slate-500">
                {status.autoRoll
                  ? `End of day starts at ${clock(status.eodTime)} (tenant time) if nobody has run it.`
                  : 'The schedule does not start end of day for this book; an operator rolls it.'}
              </p>
              <label className="flex items-center gap-2 text-slate-300">
                End-of-day time
                <input type="time" value={scheduleTime || status.eodTime.slice(0, 5)}
                  onChange={e => setScheduleTime(e.target.value)} disabled={saving}
                  className="rounded-lg border border-slate-700 bg-slate-950/60 px-2 py-1 text-slate-200" />
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={() => saveSchedule(true)} disabled={saving}
                  className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white text-sm font-semibold"
                  data-testid="eod-schedule-on">
                  {status.autoRoll ? 'Save time' : 'Turn the schedule on'}
                </button>
                {status.autoRoll && (
                  <button type="button" onClick={() => saveSchedule(false)} disabled={saving}
                    className="px-3 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 disabled:opacity-40 text-slate-300 text-sm"
                    data-testid="eod-schedule-off">
                    Turn the schedule off
                  </button>
                )}
              </div>
            </div>
            {settingsMessage && <p className="md:col-span-2 text-sm text-emerald-300">{settingsMessage}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default EndOfDayPanel;
